import { makeAutoObservable, runInAction } from 'mobx';
import agent from '../api/agent';
import { Vehicle, VehicleCreate, VehicleEdit } from '../models/Vehicle';

export default class VehicleStore {
    vehicleRegistry = new Map<number, Vehicle>();
    loadingInitial = false;
    page = 1;
    hasNext = false;
    totalCount = 0;
    groupCount: number | null = null;
    yearGroups: number[][] | null = null;
    view: 'ranges' | 'individual' = 'individual';
    loadError = '';
    private loadVersion = 0;
    private loadController?: AbortController;
    searchTerm = '';
    source = '';
    sort = 'fitment-fields asc';
    year = '';
    yearFrom = '';
    yearTo = '';
    make = '';
    model = '';
    assigned = '';
    fieldValues: Record<string, string> = {};

    constructor() {
        makeAutoObservable(this);
    }

    get vehicles() {
        return Array.from(this.vehicleRegistry.values());
    }

    loadVehicles = async (
        page: number = 1,
        query: string = this.searchTerm,
        source: string = this.source,
        sort: string = this.sort,
        year: string = this.year,
        make: string = this.make,
        model: string = this.model,
        assigned: string = this.assigned,
        fieldValues: Record<string, string> = this.fieldValues,
        view: 'ranges' | 'individual' = this.view,
        yearFrom: string = this.yearFrom,
        yearTo: string = this.yearTo
    ) => {
        const version = ++this.loadVersion;
        this.loadController?.abort();
        const controller = new AbortController();
        this.loadController = controller;
        this.view = view;
        this.loadingInitial = true;
        this.loadError = '';

        try {
            const result = await agent.Vehicles.list(
                page,
                query,
                source,
                sort,
                year,
                make,
                model,
                assigned,
                fieldValues,
                view,
                controller.signal,
                yearFrom,
                yearTo
            );

            runInAction(() => {
                if (version !== this.loadVersion) return;
                this.vehicleRegistry.clear();

                result.vehicles.forEach(v => {
                    this.vehicleRegistry.set(v.id, v);
                });

                this.page = page;
                this.searchTerm = query;
                this.source = source;
                this.sort = sort;
                this.year = year;
                this.yearFrom = yearFrom;
                this.yearTo = yearTo;
                this.make = make;
                this.model = model;
                this.assigned = assigned;
                this.fieldValues = { ...fieldValues };
                this.hasNext = result.hasNext;
                this.totalCount = result.totalCount;
                this.groupCount = result.groupCount ?? null;
                this.yearGroups = result.yearGroups ?? null;
                this.loadingInitial = false;
            });
        } catch (error) {
            runInAction(() => {
                if (version !== this.loadVersion) return;
                this.loadError = (error as { code?: string })?.code === 'ECONNABORTED'
                    ? 'Loading vehicles took too long. Try again or narrow your filters.'
                    : 'Vehicles could not be loaded. Try again.';
                this.loadingInitial = false;
            });
        } finally {
            if (version === this.loadVersion) this.loadController = undefined;
        }
    }

    create = async (vehicle: VehicleCreate) => {
        await agent.Vehicles.create(vehicle);
    }

    update = async (id: number, vehicle: VehicleEdit) => {
        await agent.Vehicles.update(id, vehicle);
    }

    delete = async (id: number) => {
        await agent.Vehicles.delete(id);
    }

    bulkDelete = async (payload: any) => {
        await agent.Vehicles.bulkDelete(payload);
    }

    filterOptions = async (
        year: string = '',
        make: string = '',
        source: string = this.source,
        fieldValues?: Record<string, string>,
        fieldOrder?: string[]
    ) => {
        return await agent.Vehicles.filterOptions(year, make, source, fieldValues, fieldOrder);
    }
}
